/**
 * CDP SDK News Plugin - Reading Tracker
 * Tracks scroll depth, time on page, and reading milestones
 */

import type {
  ArticleMetadata,
  ReadingState,
  ReadingThreshold,
  ArticleSelectors,
} from './types';
import {
  DEFAULT_ENGAGE_THRESHOLD,
  DEFAULT_READ_THRESHOLD,
  DEFAULT_SCROLL_MILESTONES,
} from './types';
import {
  addListener,
  throttle,
  getScrollPercent,
} from '../base';
import { getArticleContentElement } from './detector';
import { generateUUID } from '../../core/utils';

// =============================================================================
// READING TRACKER
// =============================================================================

/**
 * Reading tracker callback functions
 */
export interface ReadingTrackerCallbacks {
  onEngage?: (state: ReadingState) => void;
  onRead?: (state: ReadingState) => void;
  onMilestone?: (milestone: number, state: ReadingState) => void;
  onSessionEnd?: (state: ReadingState) => void;
}

/**
 * Reading tracker options
 */
export interface ReadingTrackerOptions {
  engageThreshold?: ReadingThreshold;
  readThreshold?: ReadingThreshold;
  milestones?: number[];
  selectors?: ArticleSelectors;
  updateIntervalMs?: number;
}

/**
 * Create a reading tracker for an article
 */
export function createReadingTracker(
  article: ArticleMetadata,
  callbacks: ReadingTrackerCallbacks,
  options: ReadingTrackerOptions = {}
): ReadingTrackerInstance {
  return new ReadingTrackerInstance(article, callbacks, options);
}

/**
 * Reading tracker instance
 */
export class ReadingTrackerInstance {
  private state: ReadingState;
  private callbacks: ReadingTrackerCallbacks;
  private options: Required<ReadingTrackerOptions>;
  private cleanupFns: (() => void)[] = [];
  private timeUpdateInterval: ReturnType<typeof setInterval> | null = null;
  private lastScrollPosition = 0;
  private isDestroyed = false;

  constructor(
    article: ArticleMetadata,
    callbacks: ReadingTrackerCallbacks,
    options: ReadingTrackerOptions = {}
  ) {
    this.callbacks = callbacks;
    this.options = {
      engageThreshold: { ...DEFAULT_ENGAGE_THRESHOLD, ...options.engageThreshold },
      readThreshold: { ...DEFAULT_READ_THRESHOLD, ...options.readThreshold },
      milestones: options.milestones || DEFAULT_SCROLL_MILESTONES,
      selectors: options.selectors || {},
      updateIntervalMs: options.updateIntervalMs || 1000,
    };

    // Initialize state
    this.state = {
      article,
      readingSessionId: generateUUID(),
      checkpointSeq: 0,
      viewedAt: Date.now(),
      totalTimeMs: 0,
      maxScrollDepth: 0,
      trackedMilestones: new Set(),
      milestoneTimings: [],
      engagedSent: false,
      readSent: false,
      scrollUpCount: 0,
      scrollDownCount: 0,
      isVisible: !document.hidden,
      visibleSince: document.hidden ? null : Date.now(),
      visibleTimeMs: 0,
    };

    this.setupListeners();
  }

  /**
   * Get current reading state
   */
  getState(): Readonly<ReadingState> {
    return { ...this.state };
  }

  /**
   * Destroy the tracker and clean up
   */
  destroy(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Update final time
    this.updateTime();

    // Clean up listeners
    for (const cleanup of this.cleanupFns) {
      cleanup();
    }
    this.cleanupFns = [];

    // Clear interval
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
      this.timeUpdateInterval = null;
    }

    // Notify session end
    if (this.callbacks.onSessionEnd) {
      this.callbacks.onSessionEnd(this.state);
    }
  }

  /**
   * Setup event listeners
   */
  private setupListeners(): void {
    // Scroll tracking (throttled)
    const handleScroll = throttle(() => this.onScroll(), 100);
    this.cleanupFns.push(addListener(window, 'scroll', handleScroll, { passive: true }));

    // Visibility tracking
    const handleVisibility = () => this.onVisibilityChange();
    this.cleanupFns.push(addListener(document, 'visibilitychange', handleVisibility));

    // Time tracking interval
    this.timeUpdateInterval = setInterval(() => {
      if (!this.isDestroyed) {
        this.updateTime();
        this.checkThresholds();
      }
    }, this.options.updateIntervalMs);

    // Initial scroll check
    this.onScroll();
  }

  /**
   * Handle scroll events
   */
  private onScroll(): void {
    const currentScroll = window.scrollY;
    const scrollPercent = this.getArticleScrollPercent();

    // Track scroll direction
    if (currentScroll > this.lastScrollPosition) {
      this.state.scrollDownCount++;
    } else if (currentScroll < this.lastScrollPosition) {
      this.state.scrollUpCount++;
    }
    this.lastScrollPosition = currentScroll;

    // Update max scroll depth
    if (scrollPercent > this.state.maxScrollDepth) {
      this.state.maxScrollDepth = scrollPercent;
    }

    // Check milestones
    this.checkMilestones(scrollPercent);

    // Check thresholds
    this.checkThresholds();
  }

  /**
   * Handle visibility changes
   */
  private onVisibilityChange(): void {
    const wasVisible = this.state.isVisible;
    this.state.isVisible = !document.hidden;

    if (this.state.isVisible && !wasVisible) {
      // Became visible
      this.state.visibleSince = Date.now();
    } else if (!this.state.isVisible && wasVisible) {
      // Became hidden - update visible time
      if (this.state.visibleSince) {
        this.state.visibleTimeMs += Date.now() - this.state.visibleSince;
        this.state.visibleSince = null;
      }
    }
  }

  /**
   * Update time counters
   */
  private updateTime(): void {
    const now = Date.now();
    this.state.totalTimeMs = now - this.state.viewedAt;

    // Update visible time if currently visible
    if (this.state.isVisible && this.state.visibleSince) {
      // This gives us the current visible time
      // Note: visibleTimeMs stores previously accumulated visible time
    }
  }

  /**
   * Get scroll percentage within article content
   */
  private getArticleScrollPercent(): number {
    const contentElement = getArticleContentElement(this.options.selectors);

    if (contentElement) {
      // Calculate scroll within article element
      const rect = contentElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const articleTop = rect.top + window.scrollY;
      const articleHeight = rect.height;
      const scrollTop = window.scrollY;

      // How much of the article has been scrolled past
      const scrolledInArticle = scrollTop - articleTop + viewportHeight;
      const scrollableHeight = articleHeight;

      if (scrollableHeight <= 0) return 100;

      const percent = Math.min(100, Math.max(0, (scrolledInArticle / scrollableHeight) * 100));
      return Math.round(percent);
    }

    // Fallback to page scroll
    return getScrollPercent();
  }

  /**
   * Check and fire milestone events
   */
  private checkMilestones(currentPercent: number): void {
    for (const milestone of this.options.milestones) {
      if (
        currentPercent >= milestone &&
        !this.state.trackedMilestones.has(milestone)
      ) {
        this.state.trackedMilestones.add(milestone);
        // Always record the reach timing, even when the separate milestone
        // events are folded into the session event (lean mode).
        this.state.milestoneTimings.push({
          pct: milestone,
          t: Math.round(this.state.totalTimeMs / 1000),
        });

        if (this.callbacks.onMilestone) {
          this.callbacks.onMilestone(milestone, this.state);
        }
      }
    }
  }

  /**
   * Check engage/read thresholds
   */
  private checkThresholds(): void {
    const timeSeconds = this.state.totalTimeMs / 1000;
    const scrollPercent = this.state.maxScrollDepth;

    // Check engage threshold
    if (!this.state.engagedSent) {
      const engageByScroll =
        this.options.engageThreshold.scrollPercent !== undefined &&
        scrollPercent >= this.options.engageThreshold.scrollPercent;
      const engageByTime =
        this.options.engageThreshold.timeSeconds !== undefined &&
        timeSeconds >= this.options.engageThreshold.timeSeconds;

      if (engageByScroll || engageByTime) {
        this.state.engagedSent = true;

        if (this.callbacks.onEngage) {
          this.callbacks.onEngage(this.state);
        }
      }
    }

    // Check read threshold
    if (!this.state.readSent) {
      const readByScroll =
        this.options.readThreshold.scrollPercent !== undefined &&
        scrollPercent >= this.options.readThreshold.scrollPercent;
      const readByTime =
        this.options.readThreshold.timeSeconds !== undefined &&
        timeSeconds >= this.options.readThreshold.timeSeconds;

      if (readByScroll || readByTime) {
        this.state.readSent = true;

        if (this.callbacks.onRead) {
          this.callbacks.onRead(this.state);
        }
      }
    }
  }

  /**
   * Get time spent on article in seconds
   */
  getTimeOnPageSeconds(): number {
    return Math.round(this.state.totalTimeMs / 1000);
  }

  /**
   * Get visible time in seconds
   */
  /**
   * Non-terminal checkpoint (tab hidden, page still alive): refresh the
   * counters and hand back a snapshot WITHOUT touching listeners or the
   * interval, so reading keeps accumulating when the tab returns. Returns
   * null when nothing changed since the previous checkpoint (hidden → shown →
   * hidden with no reading in between) or when there is nothing worth
   * reporting yet (no visible time and no scroll).
   */
  checkpoint(): ReadingState | null {
    if (this.isDestroyed) return null;
    this.updateTime();
    const visible = this.getVisibleTimeSeconds();
    if (visible <= 0 && this.state.maxScrollDepth <= 0) return null;
    const sig = `${visible}|${this.state.maxScrollDepth}|${this.state.readSent}|${this.state.engagedSent}|${this.state.trackedMilestones.size}|${this.state.scrollUpCount}`;
    if (sig === this.lastCheckpointSig) return null;
    this.lastCheckpointSig = sig;
    this.state.checkpointSeq += 1;
    return { ...this.state };
  }

  private lastCheckpointSig: string | null = null;

  getVisibleTimeSeconds(): number {
    let total = this.state.visibleTimeMs;

    // Add current visible session if visible
    if (this.state.isVisible && this.state.visibleSince) {
      total += Date.now() - this.state.visibleSince;
    }

    return Math.round(total / 1000);
  }

  /**
   * Get current scroll depth
   */
  getCurrentScrollDepth(): number {
    return this.getArticleScrollPercent();
  }

  /**
   * Get max scroll depth
   */
  getMaxScrollDepth(): number {
    return this.state.maxScrollDepth;
  }

  /**
   * Check if engaged
   */
  isEngaged(): boolean {
    return this.state.engagedSent;
  }

  /**
   * Check if read
   */
  isRead(): boolean {
    return this.state.readSent;
  }

  /**
   * Get tracked milestones
   */
  getTrackedMilestones(): number[] {
    return Array.from(this.state.trackedMilestones).sort((a, b) => a - b);
  }

  /**
   * Determine what triggered engagement
   */
  getEngageTrigger(): 'scroll' | 'time' | 'both' | null {
    if (!this.state.engagedSent) return null;

    const timeSeconds = this.state.totalTimeMs / 1000;
    const scrollPercent = this.state.maxScrollDepth;

    const byScroll =
      this.options.engageThreshold.scrollPercent !== undefined &&
      scrollPercent >= this.options.engageThreshold.scrollPercent;
    const byTime =
      this.options.engageThreshold.timeSeconds !== undefined &&
      timeSeconds >= this.options.engageThreshold.timeSeconds;

    if (byScroll && byTime) return 'both';
    if (byScroll) return 'scroll';
    if (byTime) return 'time';
    return null;
  }

  /**
   * Determine what triggered read
   */
  getReadTrigger(): 'scroll' | 'time' | 'both' | null {
    if (!this.state.readSent) return null;

    const timeSeconds = this.state.totalTimeMs / 1000;
    const scrollPercent = this.state.maxScrollDepth;

    const byScroll =
      this.options.readThreshold.scrollPercent !== undefined &&
      scrollPercent >= this.options.readThreshold.scrollPercent;
    const byTime =
      this.options.readThreshold.timeSeconds !== undefined &&
      timeSeconds >= this.options.readThreshold.timeSeconds;

    if (byScroll && byTime) return 'both';
    if (byScroll) return 'scroll';
    if (byTime) return 'time';
    return null;
  }
}
